import { IonIcon } from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type FieldApi } from "@tanstack/react-form";
import { toast } from "sonner";
import EmptyAvatar from "@/assets/empty_avatar.svg";
import { pencilSquareIcon } from "@/lib/icons/@heroicons/react/24/outline";
import { cn } from "@/lib/style/cva.config";
import { logger } from "@/logger";

export type CustomFieldAvatarProps = {
  label: string;
  description?: string;
  required?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: FieldApi<
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any
  >;
};

export function CustomFieldAvatar({ label, required = false, field }: CustomFieldAvatarProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const hasError = field.state.meta.errors && field.state.meta.errors.length > 0;
  // Extract error message - handle both string and ZodError object formats
  const errorMessage = hasError
    ? typeof field.state.meta.errors[0] === "string"
      ? field.state.meta.errors[0]
      : field.state.meta.errors[0]?.message || String(field.state.meta.errors[0])
    : null;

  // Focus the avatar button when there's an error and form is submitted
  useEffect(() => {
    if (hasError && field.form.state.submissionAttempts > 0) {
      // Use setTimeout to ensure the focus happens after the browser's native validation
      const timeoutId = setTimeout(() => {
        buttonRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [hasError, field.form.state.submissionAttempts]);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("fields:file.too-large", { size: "5MB" }));
      event.target.value = "";
      return;
    }

    // Validate file type
    const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
    const allowedExtensions = ["jpg", "jpeg", "png", "gif", "webp"];

    if (!allowedExtensions.includes(fileExtension)) {
      toast.error("Ongeldig bestandstype. Gebruik JPG, PNG, GIF of WEBP");
      event.target.value = "";
      return;
    }

    try {
      // Create preview URL
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);

      // Convert file to base64 for backend
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        // Store the base64 string in the field value
        field.handleChange(base64String);
      };
      reader.onerror = () => {
        logger.error("Failed to read file");
        toast.error(t("actions:update-profile-picture.read-error"));
      };
      reader.readAsDataURL(file);
    } catch (error) {
      logger.error("Failed to process image", { error });
      toast.error(t("actions:update-profile-picture.read-error"));
    }

    // Keep the file input value so users can see their selection
    // event.target.value = "";
  };

  const imageSource = previewUrl || EmptyAvatar;

  return (
    <div className="flex flex-col gap-2">
      {/* Label */}
      <label className="text-md font-semibold text-txt-primary">
        {label}
        {required && <span className="text-error-120"> *</span>}
      </label>

      {/* Avatar with edit button */}
      <div className="relative size-[120px]">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
          // Don't use native required attribute - validation is handled by TanStack Form
          // Native validation doesn't work properly with hidden inputs
        />

        <button
          ref={buttonRef}
          type="button"
          onClick={handleClick}
          className="relative size-[120px] overflow-hidden rounded-full"
          aria-required={required}
          aria-invalid={hasError}
        >
          <img src={imageSource} className="size-full rounded-full object-cover" alt="Profile" />
        </button>

        {/* Edit button overlay */}
        <button
          type="button"
          onClick={handleClick}
          className={cn(
            "absolute left-[91px] top-0 flex size-[33.333px] items-center justify-center rounded-full border-[0.778px] border-bordercolor-primary bg-white p-[5.25px] shadow-sm",
            "hover:bg-surface-primary active:bg-neutral-50",
          )}
        >
          <IonIcon icon={pencilSquareIcon} className="size-[16.667px] text-txt-secondary" />
        </button>
      </div>

      {/* Error message */}
      {hasError && (
        <p className="text-sm font-medium leading-[1.4] text-error-120">{errorMessage}</p>
      )}
    </div>
  );
}
